import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save, AlertTriangle, Search, Layers, Grid3X3, Monitor, CheckSquare, Square, Database } from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import '../styles/erp-theme.css';
import { ModuleNode, TriCheckbox, Pill } from './TierPrivilegesBlocks';
import { useTierList } from '../lib/useTierList';
import { API, apiFetch } from '../lib/api';

/** Compute minimal diff between baseline and current enabled state. */
function computePrivilegeDiff(baseline, current) {
  const changes = [];
  const pairs = [
    { key: 'modules',    entityType: 'Module'    },
    { key: 'subModules', entityType: 'SubModule' },
    { key: 'screens',    entityType: 'Screen'    },
  ];
  for (const { key, entityType } of pairs) {
    const bSet = baseline[key] || new Set();
    const cSet = current[key]  || new Set();
    for (const id of bSet) {
      if (!cSet.has(id)) changes.push({ entityType, entityId: id, enabled: false });
    }
    for (const id of cSet) {
      if (!bSet.has(id)) changes.push({ entityType, entityId: id, enabled: true });
    }
  }
  return changes;
}

// Exported for backward compatibility (LicensorConsolePricingTab imports this).
// The component itself uses useTierList() for live data.
export const TIER_ORDER = [
  'Lite', 'Express', 'Standard', 'Professional', 'Enterprise'
];

export function TierPrivilegesEditor({ industryFilter = '*' } = {}) {
  /* ── Dynamic tier list ────────────────────────────────────────────────── */
  const { tiers, colors: tierColors, loading: tiersLoading } = useTierList();

  const [selectedTier, setSelectedTier] = useState('');
  // Auto-select first tier once the list loads
  useEffect(() => {
    if (tiers.length > 0 && !selectedTier) setSelectedTier(tiers[0]);
  }, [tiers, selectedTier]);
  const [tree, setTree]   = useState(null);
  const [totals, setTotals] = useState({ modules: 0, subModules: 0, screens: 0 });
  const [enabled, setEnabled] = useState({ modules: new Set(), subModules: new Set(), screens: new Set() });
  const [expanded, setExpanded] = useState({ modules: new Set(), subModules: new Set() });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | enabled | partial | disabled
  const [levelFilter, setLevelFilter]   = useState('all'); // all | module | submodule | screen
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty]   = useState(false);

  /* Baseline = immutable snapshot of last saved/loaded state for diff computation */
  const baselineEnabled = useRef({ modules: new Set(), subModules: new Set(), screens: new Set() });

  // Industry membership check — post-2026-05-20 the tree fetch is already
  // industry-scoped (loadTree passes ?industryCode=X) so every row in the
  // tree IS "in" the selected industry. We keep `inIndustry()` as a stable
  // no-op so the downstream prop chain (`inIndustry`, `industryFiltered`) in
  // ModuleNode / SubModuleNode doesn't need a refactor.
  const inIndustry = () => true;

  // Enterprise (and any *unlimited* tier) is auto-enabled — no save needed.
  const isUnlimited = useMemo(() => {
    const t = String(selectedTier || '').toLowerCase();
    return t === 'enterprise';
  }, [selectedTier]);

  // Load tree once  (lazy per industry — post-2026-05-20 entity rows live
  // per industry, so we must fetch only the slice that matches the current
  // industry selector + the SHARED baseline. Pass industryFilter='*' to get
  // SHARED-only.)
  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const ic = industryFilter && industryFilter !== '*' ? industryFilter : 'SHARED';
      const r = await apiFetch(`${API}/uams/tree?industryCode=${encodeURIComponent(ic)}`);
      const d = await r.json();
      if (d.error) {
        erpToast.error(`Tree load failed: ${d.error}`);
      } else {
        setTree(d.tree || []);
        setTotals(d.totals || {});
      }
    } catch (e) { erpToast.error(`Tree load failed: ${e.message}`); }
    setLoading(false);
  }, [industryFilter]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // Load tier privileges whenever tier OR industry changes
  useEffect(() => {
    if (!selectedTier) return;
    const ic = industryFilter && industryFilter !== '*' ? industryFilter : 'SHARED';
    apiFetch(`${API}/license/tier-privileges/${encodeURIComponent(selectedTier)}?industryCode=${encodeURIComponent(ic)}`)
      .then(r => r.json())
      .then(rows => {
        const m = new Set(), s = new Set(), sc = new Set();
        (rows || []).forEach(p => {
          if (!p.Enabled) return;
          if (p.EntityType === 'Module')    m.add(String(p.EntityId));
          if (p.EntityType === 'SubModule') s.add(String(p.EntityId));
          if (p.EntityType === 'Screen')    sc.add(String(p.EntityId));
        });
        const loaded = { modules: m, subModules: s, screens: sc };
        setEnabled(loaded);
        // Snapshot baseline so save() sends only the diff
        baselineEnabled.current = {
          modules:    new Set(m),
          subModules: new Set(s),
          screens:    new Set(sc),
        };
        setDirty(false);
      })
      .catch(() => {});
  }, [selectedTier, industryFilter]);

  // Sync FROM source has been REMOVED — UAMS is now local-only.
  // The "Re-seed from source" button has been retired; manage modules in
  // the new UAMS Master tab instead.

  // Push tier privileges TO production (always available) -------------------
  const [pushing, setPushing] = useState(false);
  const push = async () => {
    setPushing(true);
    try {
      const r = await apiFetch(`${API}/uams/push-to-production`, { method: 'POST' });
      const d = await r.json();
      if (d.error) erpToast.error(`Push failed: ${d.error}`);
      else erpToast.success(`Pushed ${d.pushed} privileges to ${d.targetTable} on ${d.targetDB}`);
    } catch (e) { erpToast.error(`Push failed: ${e.message}`); }
    setPushing(false);
  };

  // Save tier privileges — sends ONLY the diff (changed rows), not full state
  const save = async () => {
    const ic = industryFilter && industryFilter !== '*' ? industryFilter : 'SHARED';
    const changes = computePrivilegeDiff(baselineEnabled.current, enabled);

    if (changes.length === 0) {
      erpToast.info(`${selectedTier} @ ${ic} — no changes to save`);
      setDirty(false);
      return;
    }

    setSaving(true);
    try {
      const r = await apiFetch(`${API}/license/tier-privileges/${encodeURIComponent(selectedTier)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes, industryCodes: [ic] }),
      });
      const d = await r.json();
      if (d.error) {
        erpToast.error(`Save failed: ${d.error}`);
      } else {
        erpToast.success(
          `${selectedTier} @ ${ic} — ${changes.length} change(s) · ${d.rowsChanged ?? 0} DB rows updated`
        );
        setDirty(false);
        // Advance baseline so the next diff starts from here
        baselineEnabled.current = {
          modules:    new Set(enabled.modules),
          subModules: new Set(enabled.subModules),
          screens:    new Set(enabled.screens),
        };
      }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(false);
  };

  // Toggle helpers
  const toggleScreen = (seqId) => {
    setDirty(true);
    setEnabled(prev => {
      const sc = new Set(prev.screens);
      if (sc.has(String(seqId))) sc.delete(String(seqId)); else sc.add(String(seqId));
      return { ...prev, screens: sc };
    });
  };

  const toggleSubModule = (sub) => {
    setDirty(true);
    const sid = String(sub.SubModuleId);
    const screenIds = (sub.screens || []).map(s => String(s.SeqId));
    setEnabled(prev => {
      const s = new Set(prev.subModules);
      const sc = new Set(prev.screens);
      if (s.has(sid)) {
        // Currently enabled → disable submodule + all its screens
        s.delete(sid);
        screenIds.forEach(id => sc.delete(id));
      } else {
        // Currently disabled → enable submodule + all its screens
        s.add(sid);
        screenIds.forEach(id => sc.add(id));
      }
      return { ...prev, subModules: s, screens: sc };
    });
  };

  const toggleModule = (mod) => {
    setDirty(true);
    const mid = String(mod.ModuleId);
    const allSubIds    = (mod.subModules || []).map(s => String(s.SubModuleId));
    const allScreenIds = (mod.subModules || []).flatMap(s => (s.screens || []).map(sc => String(sc.SeqId)));
    setEnabled(prev => {
      const m  = new Set(prev.modules);
      const s  = new Set(prev.subModules);
      const sc = new Set(prev.screens);
      if (m.has(mid)) {
        m.delete(mid);
        allSubIds.forEach(id => s.delete(id));
        allScreenIds.forEach(id => sc.delete(id));
      } else {
        m.add(mid);
        allSubIds.forEach(id => s.add(id));
        allScreenIds.forEach(id => sc.add(id));
      }
      return { modules: m, subModules: s, screens: sc };
    });
  };

  const selectAll = () => {
    if (!tree) return;
    setDirty(true);
    const m = new Set(), s = new Set(), sc = new Set();
    tree.forEach(mod => {
      m.add(String(mod.ModuleId));
      (mod.subModules || []).forEach(sub => {
        s.add(String(sub.SubModuleId));
        (sub.screens || []).forEach(scr => sc.add(String(scr.SeqId)));
      });
    });
    setEnabled({ modules: m, subModules: s, screens: sc });
  };
  const clearAll = () => {
    setDirty(true);
    setEnabled({ modules: new Set(), subModules: new Set(), screens: new Set() });
  };

  // Memoize diff count so computePrivilegeDiff isn't called twice per render
  const pendingDiffCount = useMemo(
    () => (dirty && !isUnlimited) ? computePrivilegeDiff(baselineEnabled.current, enabled).length : 0,
    // eslint-disable-next-line
    [enabled, dirty, isUnlimited]
  );
  const moduleState = useCallback((mod) => {
    const subs = mod.subModules || [];
    if (subs.length === 0) return 'none';
    const allScreens = subs.flatMap(s => s.screens || []);
    if (allScreens.length === 0) {
      // No screens → check submodule state
      const enabledSubs = subs.filter(s => enabled.subModules.has(String(s.SubModuleId))).length;
      if (enabledSubs === 0) return 'none';
      if (enabledSubs === subs.length) return 'all';
      return 'partial';
    }
    const enabledScreens = allScreens.filter(s => enabled.screens.has(String(s.SeqId))).length;
    if (enabledScreens === 0) return 'none';
    if (enabledScreens === allScreens.length) return 'all';
    return 'partial';
  }, [enabled]);

  const subModuleState = useCallback((sub) => {
    const screens = sub.screens || [];
    if (screens.length === 0) {
      return enabled.subModules.has(String(sub.SubModuleId)) ? 'all' : 'none';
    }
    const en = screens.filter(s => enabled.screens.has(String(s.SeqId))).length;
    if (en === 0) return 'none';
    if (en === screens.length) return 'all';
    return 'partial';
  }, [enabled]);

  // Filter tree by search + status + level
  const filteredTree = useMemo(() => {
    if (!tree) return [];
    const needle = search.toLowerCase();

    const matchesSearch = (val) => !needle || (val || '').toLowerCase().includes(needle);

    return tree.map(m => {
      const modState = moduleState(m);

      // Level: if filtering to submodule/screen only, skip top-level module row logic
      const subs = (m.subModules || []).map(sub => {
        const subSt = subModuleState(sub);
        const screens = (sub.screens || []).filter(s => {
          if (!matchesSearch(s.ScreenTitle) && !matchesSearch(s.ScreenMenuName) && !matchesSearch(String(s.SeqId))) return false;
          if (levelFilter !== 'all' && levelFilter !== 'screen') return true; // keep if level allows
          if (statusFilter === 'all') return true;
          const checked = enabled.screens.has(String(s.SeqId));
          if (statusFilter === 'enabled')  return checked;
          if (statusFilter === 'disabled') return !checked;
          return true;
        });
        const subMatchesSearch = matchesSearch(sub.SubModuleName) || matchesSearch(String(sub.SubModuleId));
        const subMatchesStatus = (() => {
          if (statusFilter === 'all') return true;
          if (statusFilter === 'enabled')  return subSt === 'all';
          if (statusFilter === 'disabled') return subSt === 'none';
          if (statusFilter === 'partial')  return subSt === 'partial';
          return true;
        })();
        if (levelFilter === 'screen') {
          return screens.length > 0 ? { ...sub, screens } : null;
        }
        if (!subMatchesSearch && screens.length === 0) return null;
        if (!subMatchesStatus && screens.length === 0 && statusFilter !== 'all') return null;
        return { ...sub, screens: levelFilter === 'submodule' ? [] : screens };
      }).filter(Boolean);

      const modMatchesSearch = matchesSearch(m.ModuleName) || matchesSearch(String(m.ModuleId));
      const modMatchesStatus = (() => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'enabled')  return modState === 'all';
        if (statusFilter === 'disabled') return modState === 'none';
        if (statusFilter === 'partial')  return modState === 'partial';
        return true;
      })();
      if (levelFilter === 'module') {
        if (!modMatchesSearch && !modMatchesStatus) return null;
        if (statusFilter !== 'all' && !modMatchesStatus) return null;
        return { ...m, subModules: [] };
      }
      if (subs.length > 0 || (modMatchesSearch && (statusFilter === 'all' || modMatchesStatus))) return { ...m, subModules: subs };
      return null;
    }).filter(Boolean);
  }, [tree, search, statusFilter, levelFilter, enabled, moduleState, subModuleState]);

  // Counts for selected tier
  const counts = useMemo(() => ({
    modules: enabled.modules.size,
    subModules: enabled.subModules.size,
    screens: enabled.screens.size
  }), [enabled]);

  const toggleExpanded = (key, id) => {
    setExpanded(prev => {
      const s = new Set(prev[key]);
      if (s.has(String(id))) s.delete(String(id)); else s.add(String(id));
      return { ...prev, [key]: s };
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Source-of-truth banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px', borderRadius: 6,
        background: 'var(--erp-grid-row-hover, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 11, color: '#1e40af'
      }}>
        <Database size={14} />
        <div>
          <strong>Master source of truth: RC26MASTERDB.WebApp · matrix model</strong> · Modules/SubModules/Screens are maintained locally (UAMS Master tab) · Saves auto-push to <code>dbo.UAMS_TierMatrix_*</code> on RC26erp_dev so the live ERP can filter via <code>WHERE T_&lt;Tier&gt; = 1</code>.
        </div>
      </div>

      {/* Tier selector strip */}
      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Editing privileges for tier:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {tiers.map(t => {
            const active = selectedTier === t;
            const color = tierColors[t] || "#64748b";
            return (
              <button
                key={t}
                onClick={() => {
                  if (dirty && !window.confirm('You have unsaved changes. Discard and switch tier?')) return;
                  setSelectedTier(t);
                }}
                className="erp-btn"
                style={{
                  background: active ? color : '#fff',
                  color: active ? '#fff' : '#1e293b',
                  borderColor: active ? color : '#cbd5e1',
                  fontWeight: active ? 700 : 600
                }}
                data-testid={`tier-tab-${t.replace(/\s/g, '-')}`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            className="form-input"
            placeholder="Search module / submodule / screen…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 34 }}
            disabled={isUnlimited}
            data-testid="tier-priv-search"
          />
        </div>

        {/* Status filter chips */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { id: 'all',      label: 'All',      color: '#64748b' },
            { id: 'enabled',  label: 'Enabled',  color: '#10b981' },
            { id: 'partial',  label: 'Partial',  color: '#f59e0b' },
            { id: 'disabled', label: 'Disabled', color: '#ef4444' },
          ].map(f => (
            <button key={f.id} onClick={() => setStatusFilter(f.id)} disabled={isUnlimited}
              data-testid={`tier-priv-status-${f.id}`}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                background: statusFilter === f.id ? f.color : `${f.color}18`,
                color: statusFilter === f.id ? '#fff' : f.color,
                border: `1px solid ${f.color}60`, transition: 'all 0.15s'
              }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Level filter */}
        <select className="form-input" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
          disabled={isUnlimited} data-testid="tier-priv-level-filter"
          style={{ minWidth: 130, fontSize: 11 }}>
          <option value="all">All Levels</option>
          <option value="module">Module only</option>
          <option value="submodule">SubModule only</option>
          <option value="screen">Screen only</option>
        </select>

        <button className="erp-btn" onClick={selectAll} disabled={isUnlimited} data-testid="tier-priv-select-all">
          <CheckSquare size={12} /> Select All
        </button>
        <button className="erp-btn" onClick={clearAll} disabled={isUnlimited} data-testid="tier-priv-clear-all">
          <Square size={12} /> Clear All
        </button>
        <button className="erp-btn" onClick={push} disabled={pushing} data-testid="tier-priv-push" title="Push current tier matrix to dbo.UAMS_TierMatrix_* on RC26erp_dev so the live ERP can read privileges per tier.">
          <Database size={12} /> {pushing ? 'Pushing…' : 'Push Matrix → Production'}
        </button>
        <button
          className="erp-btn erp-btn-primary"
          onClick={save}
          disabled={saving || !dirty || isUnlimited}
          data-testid="tier-priv-save"
          title={isUnlimited ? 'Enterprise tier has all modules enabled by default — no save required.' : ''}
        >
          <Save size={12} /> {isUnlimited ? 'No Save Needed' : (saving ? 'Saving…' : (dirty ? `Save (${pendingDiffCount} changes)` : 'Saved'))}
        </button>
      </div>

      {/* Unlimited banner */}
      {isUnlimited && (
        <div
          data-testid="tier-priv-unlimited-banner"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '14px 18px', borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.10))',
            border: '1px solid rgba(139,92,246,0.30)',
            color: 'var(--text-primary)'
          }}
        >
          <CheckSquare size={20} color="#8b5cf6" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4 }}>Enterprise — every module enabled by default</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              This tier grants access to <strong>all {totals.modules} modules · {totals.subModules} sub-modules · {totals.screens} screens</strong>.
              No privileges need to be saved. The production ERP can short-circuit ACL checks for this tier and load every screen.
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Pill icon={<Layers size={11} />}   label="Modules"     value={isUnlimited ? `${totals.modules} / ${totals.modules}` : `${counts.modules} / ${totals.modules}`} color={tierColors[selectedTier] || "#64748b"} />
        <Pill icon={<Grid3X3 size={11} />}  label="SubModules"  value={isUnlimited ? `${totals.subModules} / ${totals.subModules}` : `${counts.subModules} / ${totals.subModules}`} color={tierColors[selectedTier] || "#64748b"} />
        <Pill icon={<Monitor size={11} />}  label="Screens"     value={isUnlimited ? `${totals.screens} / ${totals.screens}` : `${counts.screens} / ${totals.screens}`} color={tierColors[selectedTier] || "#64748b"} />
        {!isUnlimited && dirty && <Pill icon={<AlertTriangle size={11} />} label="Unsaved" value={`${pendingDiffCount} change(s) pending`} color="#dc2626" />}
        {isUnlimited && <Pill icon={<CheckSquare size={11} />} label="Status" value="ALL ENABLED" color="#8b5cf6" />}
      </div>

      {/* Tree */}
      <div className="card" style={{ padding: 0, maxHeight: '60vh', overflowY: 'auto', opacity: isUnlimited ? 0.55 : 1, pointerEvents: isUnlimited ? 'none' : 'auto' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
            <Database size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: 11, marginTop: 8 }}>Loading module tree…</div>
          </div>
        ) : filteredTree.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
            No modules match the filter. Add modules / submodules / screens via the <strong>UAMS Master</strong> tab.
          </div>
        ) : (
          <div data-testid="tier-priv-tree">
            {filteredTree.map(mod => (
              <ModuleNode
                key={mod.ModuleId}
                mod={mod}
                state={moduleState(mod)}
                expanded={expanded.modules.has(String(mod.ModuleId))}
                onToggleExpand={() => toggleExpanded('modules', mod.ModuleId)}
                onToggle={() => toggleModule(mod)}
                subModuleState={subModuleState}
                expandedSubs={expanded.subModules}
                onToggleSubExpand={(id) => toggleExpanded('subModules', id)}
                onToggleSub={toggleSubModule}
                screensEnabled={enabled.screens}
                onToggleScreen={toggleScreen}
                color={tierColors[selectedTier] || "#64748b"}
                inIndustry={inIndustry}
                industryFiltered={industryFilter && industryFilter !== '*'}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}


export default TierPrivilegesEditor;
